import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Lock, Eye, EyeOff, AlertCircle } from 'lucide-react';

export const AdminLogin = ({ onLogin }) => {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const expectedPassword =
    process.env.REACT_APP_ADMIN_PASSWORD ||
    (process.env.NODE_ENV === "development" ? "dev-local-bobapet" : "");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    // Simulate authentication delay
    await new Promise(resolve => setTimeout(resolve, 800));

    if (!expectedPassword) {
      setError('Painel não configurado: defina REACT_APP_ADMIN_PASSWORD no build.');
      setIsLoading(false);
      return;
    }

    if (password === expectedPassword) {
      localStorage.setItem('bobapet_admin_auth', 'authenticated');
      onLogin(true);
    } else {
      setError('Senha incorreta. Tente novamente.');
    }
    
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black opacity-20"></div>
      
      <Card className="relative w-full max-w-md bg-white shadow-2xl">
        <CardHeader className="text-center space-y-4">
          <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto">
            <Lock className="w-8 h-8 text-white" />
          </div>
          <div>
            <CardTitle className="text-2xl font-bold text-gray-800">
              Admin Dashboard
            </CardTitle>
            <p className="text-gray-600 mt-2">
              Acesso restrito - Bobapet + 7Rosie
            </p>
          </div>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium text-gray-700">
                Senha de Administrador
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Digite a senha admin"
                  className="pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>

            {error && (
              <div className="flex items-center space-x-2 text-red-600 bg-red-50 p-3 rounded-lg">
                <AlertCircle className="w-4 h-4" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            <Button
              type="submit"
              disabled={isLoading || !password}
              className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white py-3"
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Verificando...
                </div>
              ) : (
                <>
                  <Lock className="w-4 h-4 mr-2" />
                  Entrar no Admin
                </>
              )}
            </Button>

            <div className="text-center">
              <button
                type="button"
                onClick={() => window.location.href = '/'}
                className="text-sm text-gray-500 hover:text-gray-700 underline"
              >
                ← Voltar para Landing Page
              </button>
            </div>
          </form>

          <div className="mt-6 space-y-2 p-4 bg-blue-50 rounded-lg">
            <p className="text-xs text-blue-700">
              <strong>Dica:</strong> Esta tela protege o acesso às configurações e dados dos leads.
            </p>
            {process.env.NODE_ENV === "development" ? (
              <p className="text-xs text-blue-800">
                Em <code className="rounded bg-blue-100 px-1">npm start</code>,{" "}
                <code className="rounded bg-blue-100 px-1">.env.development</code> sobrescreve o{" "}
                <code className="rounded bg-blue-100 px-1">.env</code> para a mesma chave. Senha com{" "}
                <code className="rounded bg-blue-100 px-1">$</code>: no <code className="rounded bg-blue-100 px-1">.env</code>{" "}
                usa aspas simples, ex.{" "}
                <code className="rounded bg-blue-100 px-1">{`REACT_APP_ADMIN_PASSWORD='a$b'`}</code>.
              </p>
            ) : null}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};